// packages/core-react/src/stores/index.ts

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { getStoredToken, getStoredRefreshToken } from '@tankef/api-client';
import { Party, User, type SystemNotice } from '@tankef/core';

import { withDevtools } from './middleware-utils';

// ============================================================================
// CLIENT AUTH STORE
// ============================================================================

interface ClientAuthState {
  party: Party | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setAuth: (party: Party | null, token: string, refreshToken: string) => void;
  setParty: (party: Party) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export const useClientAuthStore = create<ClientAuthState>()(
  withDevtools(
    'ClientAuth',
    persist(
      set => ({
        party: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: true,

        setAuth: (party, token, refreshToken) => {
          set({
            party,
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        },

        setParty: party => {
          set({ party });
        },

        clearAuth: () => {
          set({
            party: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        },

        setLoading: isLoading => {
          set({ isLoading });
        },
      }),
      {
        name: 'tankef-client-auth',
        storage: createJSONStorage(() => localStorage),
        partialize: state => ({
          party: state.party,
          isAuthenticated: state.isAuthenticated,
          // Tokens are NOT persisted here — they live in localStorage via
          // setStoredTokens/getStoredToken from @tankef/api-client.
          // This avoids duplicating sensitive data across two storage locations.
        }),
        onRehydrateStorage: () => state => {
          if (state) {
            // Reconvertir party de plain object a instancia de Party
            // (localStorage guarda JSON, no instancias de clase con getters)
            if (state.party) {
              state.party = Party.fromApi(state.party as unknown as Record<string, unknown>);
            }
            // Rehydrate tokens from api-client storage.
            // Fuente de verdad: localStorage (vía getStoredToken). TokenManager
            // mantiene sessionStorage en sync con localStorage en sus métodos
            // saveTokens/updateTokens/clearTokens.
            if (state.isAuthenticated) {
              const token = getStoredToken();
              const refreshToken = getStoredRefreshToken();
              if (token && refreshToken) {
                state.token = token;
                state.refreshToken = refreshToken;
              } else {
                // Tokens were cleared — reset auth state
                state.isAuthenticated = false;
                state.party = null;
              }
            }
            state.setLoading(false);
          }
        },
      }
    )
  )
);

// ============================================================================
// BACKOFFICE AUTH STORE
// ============================================================================

interface BackofficeAuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setAuth: (user: User, token: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export const useBackofficeAuthStore = create<BackofficeAuthState>()(
  withDevtools(
    'BackofficeAuth',
    persist(
      set => ({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: true,

        setAuth: (user, token, refreshToken) => {
          set({
            user,
            token,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
          });
        },

        setUser: user => {
          set({ user });
        },

        clearAuth: () => {
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        },

        setLoading: isLoading => {
          set({ isLoading });
        },
      }),
      {
        name: 'tankef-backoffice-auth',
        storage: createJSONStorage(() => localStorage),
        partialize: state => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          // Tokens NO se persisten aquí — viven en localStorage vía
          // setStoredTokens/getStoredToken (igual que el store de web).
          // Persistirlos duplicaba el dato y lo dejaba desincronizado con
          // api-client cuando se limpiaba uno solo.
        }),
        onRehydrateStorage: () => state => {
          if (!state) return;

          // Reconvert user from plain JSON object back to User class instance
          // so that methods like .can() are available after rehydration.
          if (state.user) {
            state.user = User.fromApi(state.user as unknown as Record<string, unknown>);
          }

          // Rehydrate tokens from api-client storage (igual que el store de web).
          // Fuente de verdad: localStorage vía getStoredToken. Si los tokens ya
          // no están, reseteamos la sesión en vez de confiar en el isAuthenticated
          // persistido. Esto evita el loop login↔dashboard: antes el store decía
          // "autenticado" (y seteaba la cookie) aunque el token ya no existiera,
          // así el middleware dejaba pasar a '/' y luego las APIs daban 401.
          if (state.isAuthenticated) {
            const token = getStoredToken();
            const refreshToken = getStoredRefreshToken();
            if (token && refreshToken) {
              state.token = token;
              state.refreshToken = refreshToken;
            } else {
              state.isAuthenticated = false;
              state.user = null;
            }
          }

          // Sync the session cookie synchronously during rehydration, BEFORE
          // React components render with the new auth state. This prevents a
          // redirect loop where the Next.js middleware (server-side) sees no
          // cookie while client components already act as if authenticated:
          //   1. LoginPage rehydrates → isAuthenticated=true
          //   2. LoginPage effect redirects to '/'
          //   3. Middleware reads cookies → no cookie → bounces back to /login
          //   4. Loop.
          // By writing the cookie here (not from a useEffect), the very next
          // HTTP request carries it.
          if (typeof document !== 'undefined') {
            if (state.isAuthenticated) {
              document.cookie = `backoffice_token=1; Max-Age=${60 * 60 * 12}; Path=/; SameSite=Lax`;
            } else {
              document.cookie = `backoffice_token=; Max-Age=0; Path=/; SameSite=Lax`;
            }
          }

          state.setLoading(false);
        },
      }
    )
  )
);

// ============================================================================
// NOTICES STORE
// ============================================================================

interface NoticesState {
  notices: SystemNotice[];
  dismissedIds: Set<string>;

  // Actions
  setNotices: (notices: SystemNotice[]) => void;
  addNotice: (notice: SystemNotice) => void;
  removeNotice: (id: string) => void;
  dismiss: (id: string) => void;
  clearDismissed: () => void;
}

export const useNoticesStore = create<NoticesState>()(
  withDevtools('Notices', set => ({
    notices: [],
    dismissedIds: new Set(),

    setNotices: notices => {
      set({ notices });
    },

    addNotice: notice => {
      set(state => ({
        notices: [...state.notices.filter(n => n.id !== notice.id), notice],
      }));
    },

    removeNotice: id => {
      set(state => ({
        notices: state.notices.filter(n => n.id !== id),
      }));
    },

    dismiss: id => {
      set(state => ({
        dismissedIds: new Set(state.dismissedIds).add(id),
      }));
    },

    clearDismissed: () => {
      set({ dismissedIds: new Set() });
    },
  }))
);

// ============================================================================
// TOAST STORE
// ============================================================================

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

interface ToastState {
  toasts: Toast[];

  // Actions
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

export const useToastStore = create<ToastState>()(
  withDevtools('Toast', (set, get) => ({
    toasts: [],

    addToast: toast => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const duration = toast.duration ?? 5000;

      set(state => ({
        toasts: [...state.toasts, { ...toast, id }],
      }));

      // Auto-remove after duration
      if (duration > 0) {
        setTimeout(() => {
          get().removeToast(id);
        }, duration);
      }

      return id;
    },

    removeToast: id => {
      set(state => ({
        toasts: state.toasts.filter(t => t.id !== id),
      }));
    },

    clearToasts: () => {
      set({ toasts: [] });
    },
  }))
);

// ============================================================================
// SESSION STORE
// ============================================================================

interface SessionState {
  showWarning: boolean;
  remainingTime: number;
  lastActivity: number;

  // Actions
  setWarning: (show: boolean, remainingTime?: number) => void;
  updateActivity: () => void;
  reset: () => void;
}

export const useSessionStore = create<SessionState>()(
  withDevtools('Session', set => ({
    showWarning: false,
    remainingTime: 0,
    lastActivity: Date.now(),

    setWarning: (showWarning, remainingTime = 0) => {
      set({ showWarning, remainingTime });
    },

    updateActivity: () => {
      set({ lastActivity: Date.now(), showWarning: false });
    },

    reset: () => {
      set({
        showWarning: false,
        remainingTime: 0,
        lastActivity: Date.now(),
      });
    },
  }))
);

// ============================================================================
// UI STORE (General UI state)
// ============================================================================

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';

  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

export const useUIStore = create<UIState>()(
  withDevtools(
    'UI',
    persist(
      set => ({
        sidebarOpen: true,
        sidebarCollapsed: false,
        theme: 'light',

        toggleSidebar: () => {
          set(state => ({ sidebarOpen: !state.sidebarOpen }));
        },

        setSidebarOpen: sidebarOpen => {
          set({ sidebarOpen });
        },

        toggleSidebarCollapsed: () => {
          set(state => ({ sidebarCollapsed: !state.sidebarCollapsed }));
        },

        setTheme: theme => {
          set({ theme });
        },
      }),
      {
        name: 'tankef-ui',
        storage: createJSONStorage(() => localStorage),
      }
    )
  )
);

// ============================================================================
// SETTINGS STORE (User preferences)
// ============================================================================

export * from './settings';

// ============================================================================
// UNIFIED SESSION STORE (Consolidates registration + onboarding phases)
// ============================================================================
export { useUnifiedSessionStore } from './unifiedSession';
export type {
  RegistrationStep,
  OnboardingStep as UnifiedOnboardingStep,
  SessionStep,
  UnifiedSessionData,
  UnifiedSessionState,
  ValidatedField, // Re-export for convenience
} from './unifiedSession';

// DEPRECATED: useOnboardingSessionStore removed - use useUnifiedSessionStore instead
// DEPRECATED: ValidatedField moved to unifiedSession.ts

// ============================================================================
// BREADCRUMB STORE
// ============================================================================

export * from './breadcrumb';

// ============================================================================
// CATALOGS STORE
// ============================================================================

export * from './catalogs';

// ============================================================================
// TRANSFER STORES
// ============================================================================

export * from './transfers';
export * from './transactionLimits';
export * from './pendingPaymentOrders';

// ============================================================================
// NOTIFICATIONS STORE
// ============================================================================

export * from './notifications';

// ============================================================================
// TOUR GUIDE STORE
// ============================================================================
export * from './tourGuide';

// ============================================================================
// CURRENT ACCOUNT STORE (PM multi-cuenta, F7.4)
// ============================================================================
export * from './account';
